import { useCallback, useEffect } from 'react';
import { type FileRejection, useDropzone } from 'react-dropzone';

import { ArrowUpTrayIcon, ExclamationCircleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { DocumentIcon } from '@heroicons/react/24/outline';
import cn from 'classnames';

import { currencyFormatter } from '@/utils/currencyFormatter';

import type { GroupSubscriptionData } from '../groupSubscriptionTypes';
import { ManageSeatsLink } from './ManageSeatsLink';
import { computeProratedCents, seatsRemaining as computeSeatsRemaining } from './seatMath';

const NON_CSV_ERROR_MESSAGE = 'Please upload a valid CSV file.';

/**
 * Upload-flow state machine. The modal is presentation-only — the host owns
 * which step we're in and renders accordingly. Real-upload wiring lives in the
 * consumer (e.g. orchid-v2's `useFakeCsvUpload`, replaced when BEE-18931 lands
 * a real upload endpoint).
 */
export type CsvFlowState =
  | { step: 'upload'; error?: string }
  | { step: 'uploading'; filename: string; progress: number }
  | { step: 'review'; filename: string; emailCount: number };

type BulkInviteCsvModalProps = {
  isOpen: boolean;
  group: GroupSubscriptionData | null;
  onCancel: () => void;
  /**
   * Within capacity: send invites for the parsed list. Real wiring lands with
   * `CreateInvitation` (BEE-18931); for now the parent closes the modal.
   */
  onSubmit?: (groupId: string, emails: string[]) => void;
  /**
   * Over capacity: hand off to the seat-purchase confirmation flow.
   */
  onAddSeatsAndInvite?: (groupId: string, emails: string[], extraSeats: number) => void;
  onOpenChange: (open: boolean) => void;
  isLoading?: boolean;
  /** Upload-flow state, owned by the host so a real upload endpoint can be
   *  swapped in without touching the modal. */
  uploadState: CsvFlowState;
  /**
   * Called with the dropped/selected File once the dropzone has accepted it.
   * The host owns the actual upload — it can read `file.name` for display and
   * stream the body to the backend. (Earlier revision passed only the filename;
   * the real bulk-invite endpoint needs the bytes, hence the contract change.)
   */
  onStartUpload: (file: File) => void;
  /** Reset back to the upload step (e.g. user clicked the X on the file row). */
  onClearUpload: () => void;
  /** Surface a validation error on the upload step (e.g. dropped a non-CSV file). */
  onUploadError: (message: string) => void;
  /** Parsed email list for the over-capacity hand-off; empty until review step. */
  parsedEmails: string[];
  /** Opens the Manage Seats modal from the inline link next to "X seats remaining".
   *  Single-slot modal state in the host means this replaces (not stacks) this modal. */
  onManageSeats?: () => void;
  /** i18n lookup dict — same shape used by GroupSubscriptionsElement. Only
   *  `group_subscriptions.card.manage_seats` is consumed here. */
  translations?: Record<string, string>;
};

export function BulkInviteCsvModal({
  isOpen,
  group,
  onCancel,
  onSubmit,
  onAddSeatsAndInvite,
  onOpenChange,
  isLoading = false,
  uploadState,
  onStartUpload,
  onClearUpload,
  onUploadError,
  parsedEmails,
  onManageSeats,
  translations,
}: BulkInviteCsvModalProps) {
  const onDrop = useCallback(
    (accepted: File[], rejections: FileRejection[]) => {
      if (accepted[0]) onStartUpload(accepted[0]);
      else if (rejections.length > 0) onUploadError(NON_CSV_ERROR_MESSAGE);
    },
    [onStartUpload, onUploadError],
  );

  // `accept` matches BOTH the MIME key and any extension in its array. Windows
  // commonly reports `.csv` as `application/vnd.ms-excel` (or empty MIME), so
  // the extension half of the rule catches those without false-rejecting.
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'] },
    multiple: false,
    maxFiles: 1,
    noClick: true,
    noKeyboard: true,
  });

  // ESC closes. Disabled while uploading to avoid orphaning the timer mid-flight.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && uploadState.step !== 'uploading') onOpenChange(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onOpenChange, uploadState.step]);

  if (!isOpen || !group) return null;

  const formatMoney = (cents: number) => currencyFormatter({ value: cents / 100, currency: group.currency });

  const seatsRemaining = computeSeatsRemaining(group);
  const reviewCount = uploadState.step === 'review' ? uploadState.emailCount : 0;
  const exceedsCapacity = reviewCount > seatsRemaining;
  const extraSeatsNeeded = Math.max(0, reviewCount - seatsRemaining);

  const submitLabel = (() => {
    if (uploadState.step !== 'review') return 'Send invite';
    if (exceedsCapacity) return `Add ${extraSeatsNeeded} ${extraSeatsNeeded === 1 ? 'seat' : 'seats'} and invite`;
    return `Send ${reviewCount} ${reviewCount === 1 ? 'invite' : 'invites'}`;
  })();

  const canSubmit = !isLoading && uploadState.step === 'review' && reviewCount > 0;

  const handleSubmit = () => {
    if (!canSubmit || uploadState.step !== 'review') return;
    if (exceedsCapacity) {
      onAddSeatsAndInvite?.(group.id, parsedEmails, extraSeatsNeeded);
    } else {
      onSubmit?.(group.id, parsedEmails);
    }
  };

  const proratedCents = exceedsCapacity ? computeProratedCents(group, extraSeatsNeeded) : 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="bulk-csv-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
    >
      <div className="flex w-full flex-col gap-6 overflow-hidden bg-white p-6 shadow-lg sm:w-[640px] sm:rounded-md">
        <div className="flex flex-col gap-1">
          <h1 id="bulk-csv-title" className="text-lg font-medium text-gray-900">
            Bulk invite with CSV
          </h1>
          <p className="text-sm text-gray-500">
            {seatsRemaining} {seatsRemaining === 1 ? 'seat' : 'seats'} remaining
            {uploadState.step === 'review' && reviewCount > 0 && !exceedsCapacity && (
              <>
                {' '}
                (inviting {reviewCount} of {seatsRemaining})
              </>
            )}
            {onManageSeats && (
              <>
                {'. '}
                <ManageSeatsLink
                  onClick={onManageSeats}
                  label={translations?.['group_subscriptions.card.manage_seats'] ?? 'Manage seats'}
                />
              </>
            )}
          </p>
        </div>

        {uploadState.step === 'upload' && (
          <>
            <div className="flex flex-col gap-2">
              <div
                {...getRootProps()}
                data-testid="bulk-csv-dropzone"
                className={cn(
                  'flex h-[132px] w-full flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed py-6 px-3 text-sm transition-colors',
                  isDragActive
                    ? 'border-gray-300 bg-gray-100 text-gray-900'
                    : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300 hover:bg-gray-100 hover:text-gray-900',
                )}
              >
                <input {...getInputProps()} data-testid="bulk-csv-file-input" />
                <div className="flex flex-col items-center gap-1">
                  <DocumentIcon className="w-6 h-6" aria-hidden="true" />
                  <span className="text-xs">{isDragActive ? 'Drop here' : 'Drag and drop .CSV file'}</span>
                </div>
                {!isDragActive && (
                  <button
                    type="button"
                    onClick={open}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-900 shadow-sm hover:bg-gray-50"
                  >
                    <ArrowUpTrayIcon className="w-3.5 h-3.5" aria-hidden="true" />
                    Upload
                  </button>
                )}
              </div>
              <p className="text-sm font-light text-gray-900">
                Create a list with an <span className="font-mono">{"'Email'"}</span> column for all addresses, then save
                it as a CSV file, and upload the file.
              </p>
            </div>
            {uploadState.error && (
              <div
                role="alert"
                className="flex gap-3 p-3 text-sm text-red-900 border border-red-200 rounded-md bg-red-50"
              >
                <ExclamationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" aria-hidden="true" />
                <p>{uploadState.error}</p>
              </div>
            )}
          </>
        )}

        {uploadState.step === 'uploading' && (
          <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-md">
            <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 bg-gray-100 rounded-md">
              <span
                className="block w-5 h-5 border-2 border-gray-300 rounded-full animate-spin border-t-gray-700"
                role="status"
                aria-label="Uploading"
              />
            </div>
            <div className="flex flex-col self-stretch justify-between flex-1 overflow-hidden">
              <p className="text-sm font-medium text-gray-900 truncate">{uploadState.filename}</p>
              <p className="text-sm text-gray-500">Uploading {uploadState.progress}%</p>
            </div>
            <button
              type="button"
              onClick={onClearUpload}
              aria-label="Cancel upload"
              className="flex items-center justify-center flex-shrink-0 w-8 h-8 text-gray-500 border border-gray-200 rounded-md hover:bg-gray-50"
            >
              <XMarkIcon className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        )}

        {uploadState.step === 'review' && (
          <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-md">
            <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 bg-gray-100 rounded-md">
              <DocumentIcon className="w-6 h-6 text-gray-500" aria-hidden="true" />
            </div>
            <div className="flex flex-col self-stretch justify-between flex-1 overflow-hidden">
              <p className="text-sm font-medium text-gray-900 truncate">{uploadState.filename}</p>
              <p className="text-sm text-gray-500">
                Found {uploadState.emailCount} valid {uploadState.emailCount === 1 ? 'email' : 'emails'}
              </p>
            </div>
            <button
              type="button"
              onClick={onClearUpload}
              aria-label="Remove file"
              className="flex items-center justify-center flex-shrink-0 w-8 h-8 text-gray-500 border border-gray-200 rounded-md hover:bg-gray-50"
            >
              <XMarkIcon className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        )}

        {uploadState.step === 'review' && exceedsCapacity && (
          <div className="flex gap-3 p-3 text-sm text-indigo-900 border border-indigo-200 rounded-md bg-indigo-50">
            <InformationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-indigo-500" aria-hidden="true" />
            <div className="flex flex-col gap-1">
              <p className="font-medium">
                You need to purchase {extraSeatsNeeded} more {extraSeatsNeeded === 1 ? 'seat' : 'seats'} to invite
                everyone
              </p>
              <p>
                Add {extraSeatsNeeded} more {extraSeatsNeeded === 1 ? 'seat' : 'seats'} at{' '}
                {currencyFormatter({ value: group.per_seat_amount_cents / 100, currency: group.currency })} per
                seat/year
                {proratedCents > 0 && <> ({formatMoney(proratedCents)} prorated today)</>}.
              </p>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            disabled={isLoading || uploadState.step === 'uploading'}
            onClick={onCancel}
            className="px-3 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className="px-3 py-2 text-sm font-medium rounded-md shadow-sm bg-wt-primary text-wt-text-on-primary hover:opacity-90 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 disabled:hover:opacity-100"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
